import { Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { FORMFIELD_DEFAULT_OPTIONS, FormfieldDefaultOptions, NxFormfieldModule } from '@aposin/ng-aquila/formfield';
import { NxHeadlineModule } from '@aposin/ng-aquila/headline';
import { NxInputModule } from '@aposin/ng-aquila/input';
import { NxMessageModule } from '@aposin/ng-aquila/message';
import { NX_MODAL_DATA } from '@aposin/ng-aquila/modal';
import { NxRadioModule } from '@aposin/ng-aquila/radio-button';
import { NxRadioToggleModule } from '@aposin/ng-aquila/radio-toggle';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';

import { DocumentHandler } from '#components/DocumentHandler';
import { FileUploaderComponent } from '#components/file-uploader/file-uploader.component';
import { NotificationsGroupComponent } from '#components/notifications-group/notifications-group.component';
import { ESG_GERL_QUIZ_COMMENT_TEMPLATE, esgGerlQuizCommentChangedValidator } from '#data/esg-gerl-quiz-comment';
import { Notification } from '#models/MicroController.model';
import CveService from '#services/cve/cve.service';
import { formatDate } from '#utils/dateUtils';

import { UpdateModalSubmitParams } from '../update-modal.component';

const formfieldDefaultOptions: FormfieldDefaultOptions = {
  appearance: 'outline',
  nxFloatLabel: 'always',
};

@UntilDestroy()
@Component({
  templateUrl: './esg-gerl-update-modal.component.html',
  imports: [
    NxHeadlineModule,
    NxFormfieldModule,
    FormsModule,
    NxInputModule,
    ReactiveFormsModule,
    NxButtonModule,
    NxRadioToggleModule,
    NxRadioModule,
    NxMessageModule,
    NotificationsGroupComponent,
    FileUploaderComponent,
    NxSpinnerModule,
    TranslateModule,
  ],
  providers: [{ provide: FORMFIELD_DEFAULT_OPTIONS, useValue: formfieldDefaultOptions }],
})
export class EsgGerlUpdateModalComponent extends DocumentHandler {
  readonly data = inject<{
    setShouldClose: (val: boolean) => void;
    submit: (submitParams: UpdateModalSubmitParams) => Promise<void>;
    notifications: Notification[];
    controllerI18n: string;
    closeModal: () => void;
    email?: string;
    hasEscalationEmailInput?: boolean;
    comment?: string;
  }>(NX_MODAL_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly cveService = inject(CveService);

  private readonly iterationSignal = toSignal(this.cveService.iterationData$);

  protected comment?: string;
  protected loading = false;
  protected readonly updateForm;

  constructor() {
    super();

    const iterationData = this.iterationSignal();
    if (!iterationData) {
      throw new Error('No iteration data');
    }
    this.updateForm = this.fb.group({
      checkResponse: ['', Validators.required],
      esgGerlQuizComment: [ESG_GERL_QUIZ_COMMENT_TEMPLATE],
    });
    this.updateForm
      .get('checkResponse')
      ?.valueChanges?.pipe(untilDestroyed(this))
      ?.subscribe((value) => {
        if (value === 'reject') {
          this.updateForm
            .get('esgGerlQuizComment')
            ?.setValidators([Validators.required, esgGerlQuizCommentChangedValidator]);
        } else {
          this.updateForm.get('esgGerlQuizComment')?.clearValidators();
        }
        this.updateForm.get('esgGerlQuizComment')?.updateValueAndValidity();
      });

    if (iterationData.customProperties.esgGerlQuizComment) {
      this.updateForm.patchValue({ esgGerlQuizComment: iterationData.customProperties.esgGerlQuizComment as string });
    }
  }

  protected async submit() {
    const iterationData = this.iterationSignal();
    if (!iterationData) {
      throw new Error('No iteration data');
    }
    const isValidate = this.updateForm.value.checkResponse === 'accept';

    this.loading = true;
    this.data.setShouldClose(false);

    if (!isValidate) {
      await this.cveService.patchIteration(iterationData.iterationId, {
        customProperties: { esgGerlQuizComment: this.updateForm.value.esgGerlQuizComment },
      });
    }
    await this.data.submit({
      isValidate,
      comment: this.comment,
      documents: this.documents,
    });

    this.loading = false;
    this.data.setShouldClose(true);
    this.data.closeModal();
  }

  protected get todaysDate() {
    return formatDate(new Date().toString());
  }
}
